import React, { useContext, useEffect, useMemo, useState } from 'react'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormCheckboxField,
  FormDateField,
  FormField,
  FormMultiSelectField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { ChevronDown } from 'lucide-react'
import { FormSubmitValues, UrlPattern } from 'utils/formValidation'

import { NameDto } from '../../../api/Common/Types'
import { TickmillCompanyDto } from '../../../api/Identity/Types/Company'
import { PermissionsEnum } from '../../../api/Identity/Types/PermissionsEnum'
import {
  BonusCampaignAvailabilityPeriod,
  BonusCampaignCountdownTrigger,
  BonusCampaignExcludeType,
  BonusLabel,
} from '../../../api/Wallet/Types/BonusCampaignTypes'
import {
  BonusCampaignDto,
  BonusCampaignsDto,
  BonusCampaignStatus,
  EditCampaignDetailsFormValues,
} from '../../../api/Wallet/Types/MarketingBonusCampaignDto'
import { WalletCurrencyDto } from '../../../api/Wallet/Types/WalletCurrencyDto'
import { AuthSessionContext } from '../../../utils/AuthContext'
import { hasPermission } from '../../../utils/permissionsValidator'

const BonusCampaignDetailsFormUI: React.FC<
  FormikProps<EditCampaignDetailsFormValues> & OuterProps
> = (props) => {
  const {
    values,
    resetForm,
    handleSubmit,
    setFieldValue,
    isSubmitting,
    errors,
    tickmillCompanies,
    bonusTypes,
    currencies,
    dateFormatType,
    bonusLabels,
    bonusCampaignExcludeTypes,
    bonusCampaignAvialabilityPeriods,
    bonusCampaignCountdownTriggers,
  } = props

  const [isEdit, setEdit] = useState<boolean>(!props.data)
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const [ibTextInput, setIbTextInput] = useState('')
  const [affiliateTextInput, setAffiliateTextInput] = useState('')
  const [utmTextInput, setUtmTextInput] = useState('')

  useEffect(() => {
    setIbTextInput(getIBDisplayValue())
  }, [values.bonusCampaignIntroducingBrokers])

  useEffect(() => {
    setAffiliateTextInput(getAffiliateDisplayValue())
  }, [values.bonusCampaignAffiliates])

  useEffect(() => {
    setUtmTextInput(getUtmDisplayValue())
  }, [values.bonusCampaignUtms])

  function handleOnSetIBs(ibText: string): void {
    const ibArray = ibText
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0)

    setFieldValue('bonusCampaignIntroducingBrokers', ibArray)
  }

  function handleOnSetAffiliates(affiliateText: string): void {
    const affiliateArray = affiliateText
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0)

    setFieldValue('bonusCampaignAffiliates', affiliateArray)
  }

  function handleOnSetUtms(utmText: string): void {
    const utmArray = utmText
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0)

    setFieldValue('bonusCampaignUtms', utmArray)
  }

  const handleSetCompany = (selected: number[]) => {
    setFieldValue('tickmillCompanyIds', selected)
    props.getTickmillCompanyCurrency(selected)
  }

  const handleIbTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIbTextInput(e.target.value)
  }

  const handleAffiliateTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAffiliateTextInput(e.target.value)
  }

  const handleUtmTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUtmTextInput(e.target.value)
  }

  const handleIbTextBlur = () => {
    handleOnSetIBs(ibTextInput)
  }

  const handleAffiliateTextBlur = () => {
    handleOnSetAffiliates(affiliateTextInput)
  }

  const handleUtmTextBlur = () => {
    handleOnSetUtms(utmTextInput)
  }

  const handleSubmitForm = () => {
    if (!isSubmitting) {
      handleSubmit()
    }
  }

  const handleCancel = () => {
    setEdit(false)
    resetForm()
  }

  const getIBDisplayValue = () => {
    if (Array.isArray(values.bonusCampaignIntroducingBrokers)) {
      return values.bonusCampaignIntroducingBrokers.join(', ')
    }
    return ''
  }

  const getAffiliateDisplayValue = () => {
    if (Array.isArray(values.bonusCampaignAffiliates)) {
      return values.bonusCampaignAffiliates.join(', ')
    }
    return ''
  }

  const getUtmDisplayValue = () => {
    if (Array.isArray(values.bonusCampaignUtms)) {
      return values.bonusCampaignUtms.join(', ')
    }
    return ''
  }

  const isBonusCampaignAvailabilityPeriodIdSelected = useMemo(() => {
    return (
      values.bonusCampaignAvailabilityPeriodId !== null &&
      values.bonusCampaignAvailabilityPeriodId > 0
    )
  }, [values.bonusCampaignAvailabilityPeriodId])

  useEffect(() => {
    if (!isBonusCampaignAvailabilityPeriodIdSelected) {
      setFieldValue('value', null)
      setFieldValue('bonusCampaignCountdownTriggerId', null)
    }
  }, [isBonusCampaignAvailabilityPeriodIdSelected])

  const isBonusCampaignExcludeTypeIdSelected = useMemo(() => {
    return values.bonusCampaignExcludeTypeId !== null && values.bonusCampaignExcludeTypeId > 0
  }, [values.bonusCampaignExcludeTypeId])

  useEffect(() => {
    if (isBonusCampaignExcludeTypeIdSelected) {
      setIbTextInput('')
      setAffiliateTextInput('')
      setFieldValue('bonusCampaignIntroducingBrokers', [])
      setFieldValue('bonusCampaignAffiliates', [])
    }
  }, [isBonusCampaignExcludeTypeIdSelected])

  const labelOptions = useMemo(
    () =>
      (bonusLabels || [])
        .filter((label) => label.isAvailableForBonusCampaigns)
        .map((label) => ({ id: label.id, name: label.name })),
    [bonusLabels]
  )

  const selectedPaymentMethodValue =
    values.campaignPaymentMethods.length === 1
      ? values.campaignPaymentMethods[0]?.id || 'all'
      : values.campaignPaymentMethods.length === props.data.campaignPaymentMethods.length
        ? 'all'
        : 'all'

  const renderMenu = () => (
    <DropdownMenuItem
      onSelect={() => props.setAuditLogModal(true)}
      data-test='audit-log-menu-button'
      disabled={!hasPermission(permissions, PermissionsEnum.CanViewBonusCampaigns)}
    >
      Audit Log
    </DropdownMenuItem>
  )

  return (
    <div className='px-4 md:px-6'>
      <Form>
        <DataGrid
          rightAddon={
            <ButtonGroup>
              {isEdit ? (
                <>
                  <Button
                    type='button'
                    variant='outline'
                    className='is-danger'
                    onClick={handleCancel}
                    data-test='cancel-button'
                  >
                    Cancel
                  </Button>
                  <Button
                    type='button'
                    onClick={handleSubmitForm}
                    data-test='save-button'
                    disabled={isSubmitting}
                  >
                    Save
                  </Button>
                </>
              ) : (
                <Button
                  type='button'
                  onClick={() => setEdit(true)}
                  data-test='edit-button'
                  disabled={!hasPermission(permissions, PermissionsEnum.CanEditBonusCampaigns)}
                >
                  Edit
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button
                      type='button'
                      variant='outline'
                      size='icon'
                      aria-label='More options'
                      className='more-button'
                    />
                  }
                >
                  <ChevronDown />
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>{renderMenu()}</DropdownMenuContent>
              </DropdownMenu>
            </ButtonGroup>
          }
        >
          <DataColumn>
            <DataCard title='General Setting'>
              <FormField
                name='internalName'
                label='Internal Name'
                error={errors.internalName}
                readOnly={!isEdit}
              />
              <FormField
                name='name'
                label='Bonus name (Client Facing)'
                error={errors.name}
                readOnly={!isEdit}
              />
              <FormMultiSelectField
                name='tickmillCompanyIds'
                label='Operating Company / Entity'
                value={values.tickmillCompanyIds}
                options={tickmillCompanies}
                readOnly={!isEdit}
                onChange={(value) =>
                  handleSetCompany(Array.isArray(value) ? (value as number[]) : [])
                }
              />
              <FormDateField
                name='startDate'
                label='Start date'
                readOnly={!isEdit}
                showTimeSelect
                isClearable={false}
                dateFormatType={dateFormatType}
                error={errors.startDate}
                value={values.startDate ? new Date(values.startDate) : null}
              />
              <FormDateField
                name='endDate'
                label='End date'
                readOnly={!isEdit}
                showTimeSelect
                isClearable={false}
                dateFormatType={dateFormatType}
                error={errors.endDate}
                value={values.endDate ? new Date(values.endDate) : null}
              />
              <FormSelectField
                name='bonusCampaignStatusId'
                label='Status'
                value={values.bonusCampaignStatusId}
                options={BonusCampaignStatus}
                readOnly={!isEdit}
                onChange={(value) => setFieldValue('bonusCampaignStatusId', Number(value))}
              />
              <FormSelectField
                name='bonusLabelId'
                label='Label'
                value={values.bonusLabelId}
                options={labelOptions}
                readOnly={!isEdit}
                error={errors.bonusLabelId}
                onChange={(value) => setFieldValue('bonusLabelId', Number(value))}
              />
            </DataCard>

            <DataCard title='Eligibility'>
              <FormSelectField
                name='campaignPaymentMethods'
                label='Deposit Method'
                value={selectedPaymentMethodValue}
                readOnly={!isEdit}
                options={[{ id: 'all', name: 'All' }, ...props.data.campaignPaymentMethods]}
                onChange={(value) => {
                  if (!value || value === 'all') {
                    setFieldValue('campaignPaymentMethods', [])
                    return
                  }

                  const paymentMethod = props.data.campaignPaymentMethods.find(
                    (option) => option.id === value
                  )
                  setFieldValue(
                    'campaignPaymentMethods',
                    paymentMethod ? [{ id: paymentMethod.id, name: paymentMethod.name }] : []
                  )
                }}
              />
              <FormSelectField
                name='defaultCurrencyId'
                label='Currency'
                value={values.defaultCurrencyId ?? ''}
                readOnly={!isEdit}
                allowClear
                options={currencies}
                onChange={(value) => setFieldValue('defaultCurrencyId', value || null)}
              />
              <FormCheckboxField
                name='noPreviousDeposits'
                label='No Previous Deposits (FTD)'
                value={values.noPreviousDeposits}
                readOnly={!isEdit}
              />
              <FormField
                name='minTotalTradedLots'
                label='Minimum Total Traded Lots'
                type='number'
                readOnly={!isEdit}
              />
              <FormField
                name='maxTotalPnl'
                label='Maximum Profile Total PNL'
                type='number'
                readOnly={!isEdit}
              />
              <FormCheckboxField
                name='targetOnlyInactiveClients'
                label='Target only inactive clients'
                value={values.targetOnlyInactiveClients}
                readOnly={!isEdit}
              />
              <FormSelectField
                name='bonusCampaignExcludeTypeId'
                label='Exclude'
                value={values.bonusCampaignExcludeTypeId}
                options={bonusCampaignExcludeTypes}
                readOnly={!isEdit}
                onChange={(value) => setFieldValue('bonusCampaignExcludeTypeId', Number(value))}
              />
              <FormField
                name='ibSelectionText'
                label='IB Selection'
                value={ibTextInput}
                placeholder='Enter IB codes separated by commas (e.g., IB66470039, IB99468989)'
                readOnly={!isEdit}
                disabled={isBonusCampaignExcludeTypeIdSelected}
                onChange={handleIbTextChange}
                onBlur={handleIbTextBlur}
              />
              <FormField
                name='affiliateSelectionText'
                label='Affiliate Selection'
                value={affiliateTextInput}
                placeholder='Enter affiliate codes separated by commas (e.g., 35076, 35170)'
                readOnly={!isEdit}
                disabled={isBonusCampaignExcludeTypeIdSelected}
                onChange={handleAffiliateTextChange}
                onBlur={handleAffiliateTextBlur}
              />
              <FormField
                name='utmSelectionText'
                label='Campaign UTM'
                value={utmTextInput}
                placeholder='Enter UTM codes separated by commas (e.g., utm_source1, utm_campaign1)'
                readOnly={!isEdit}
                onChange={handleUtmTextChange}
                onBlur={handleUtmTextBlur}
              />
            </DataCard>
          </DataColumn>

          <DataColumn>
            <DataCard title='Bonus Setup'>
              <FormSelectField
                name='bonusTypeId'
                label='Bonus Type'
                value={values.bonusTypeId}
                options={bonusTypes}
                readOnly={!isEdit}
                error={errors.bonusTypeId}
                onChange={(value) => setFieldValue('bonusTypeId', value)}
              />
              <FormField
                name='termsAndConditionsUrl'
                error={errors.termsAndConditionsUrl}
                readOnly={!isEdit}
                label='Link to T&C'
              />

              <FormField
                name='minDeposit'
                label='Minimum deposit'
                type='number'
                error={errors.minDeposit}
                readOnly={!isEdit}
              />
              <FormField
                name='maxBonus'
                error={errors.maxBonus}
                label='Maximum bonus'
                type='number'
                readOnly={!isEdit}
              />
            </DataCard>

            <DataCard title='Lifecycle'>
              <FormDateField
                name='bonusExpirationDate'
                label='Period (Bonus expiration date)'
                readOnly={!isEdit}
                showTimeSelect
                isClearable={false}
                dateFormatType={dateFormatType}
                error={errors.bonusExpirationDate}
                value={values.bonusExpirationDate ? new Date(values.bonusExpirationDate) : null}
              />
              <FormSelectField
                name='bonusCampaignAvailabilityPeriodId'
                label='Availability (Claim) Period'
                value={values.bonusCampaignAvailabilityPeriodId ?? undefined}
                options={bonusCampaignAvialabilityPeriods}
                readOnly={!isEdit}
                error={errors.bonusCampaignAvailabilityPeriodId}
                onChange={(value) =>
                  setFieldValue('bonusCampaignAvailabilityPeriodId', value ? Number(value) : 0)
                }
              />
              <FormField
                name='value'
                label='Value'
                type='number'
                readOnly={!isEdit}
                error={errors.value}
                disabled={!isBonusCampaignAvailabilityPeriodIdSelected}
              />
              <FormSelectField
                name='bonusCampaignCountdownTriggerId'
                label='Countdown trigger'
                value={values.bonusCampaignCountdownTriggerId ?? undefined}
                options={bonusCampaignCountdownTriggers}
                allowClear
                readOnly={!isEdit}
                error={errors.bonusCampaignCountdownTriggerId}
                disabled={!isBonusCampaignAvailabilityPeriodIdSelected}
                onChange={(value) =>
                  setFieldValue('bonusCampaignCountdownTriggerId', value ? Number(value) : null)
                }
              />
              <FormField
                name='validDaysAfterActivation'
                label='Valid Days After Activation'
                error={errors.validDaysAfterActivation}
                type='number'
                readOnly={!isEdit}
              />
              <FormField
                name='firstTradeRequirementFromDays'
                label='First trade requirement (days)'
                type='number'
                placeholder='Optional - leave empty for no requirement'
                readOnly={!isEdit}
              />
              <FormCheckboxField
                name='availableOnlyOnce'
                label='Available only once'
                value={values.availableOnlyOnce}
                readOnly={!isEdit}
              />
            </DataCard>
          </DataColumn>
        </DataGrid>
      </Form>
    </div>
  )
}

interface OuterProps {
  data: BonusCampaignsDto
  tickmillCompanies: TickmillCompanyDto[]
  bonusTypes: NameDto[]
  currencies: WalletCurrencyDto[]
  dateFormatType?: string
  bonusLabels: BonusLabel[]
  bonusCampaignExcludeTypes: BonusCampaignExcludeType[]
  bonusCampaignAvialabilityPeriods: BonusCampaignAvailabilityPeriod[]
  bonusCampaignCountdownTriggers: BonusCampaignCountdownTrigger[]
  getTickmillCompanyCurrency(companiesIds: number[]): void

  setAuditLogModal(value: boolean): void

  onSubmit(values: FormSubmitValues<EditCampaignDetailsFormValues>): Promise<void>
}

function dataToSubmit(values: EditCampaignDetailsFormValues): FormSubmitValues<BonusCampaignDto> {
  return {
    id: values.id,
    name: values.name,
    minDeposit: values.minDeposit,
    maxBonus: values.maxBonus,
    termsAndConditionsUrl: values.termsAndConditionsUrl,
    startDate: values.startDate,
    endDate: values.endDate,
    bonusExpirationDate: values.bonusExpirationDate,
    bonusTypeId: values.bonusTypeId,
    bonusPercent: values.bonusPercent,
    bonusAmount: values.bonusAmount,
    bonusCampaignStatusId: values.bonusCampaignStatusId,
    defaultCurrencyId: values.defaultCurrencyId || null,
    campaignPaymentMethods: values.campaignPaymentMethods?.map((method) => ({
      id: method.id,
      name: method.name,
    })),
    tickmillCompanyIds: values.tickmillCompanyIds,
    noPreviousDeposits: values.noPreviousDeposits,
    availableOnlyOnce: values.availableOnlyOnce,
    minTotalTradedLots: values.minTotalTradedLots,
    maxTotalPnl: values.maxTotalPnl,
    validDaysAfterActivation: values.validDaysAfterActivation,
    firstTradeRequirementFromDays: values.firstTradeRequirementFromDays,
    targetOnlyInactiveClients: values.targetOnlyInactiveClients,
    bonusCampaignIntroducingBrokers: values.bonusCampaignIntroducingBrokers || [],
    bonusCampaignAffiliates: values.bonusCampaignAffiliates || [],
    bonusCampaignUtms: values.bonusCampaignUtms || [],
    internalName: values.internalName,
    bonusLabelId: values.bonusLabelId,
    bonusCampaignExcludeTypeId: values.bonusCampaignExcludeTypeId,
    bonusCampaignAvailabilityPeriodId: values.bonusCampaignAvailabilityPeriodId,
    value: values.value,
    bonusCampaignCountdownTriggerId: values.bonusCampaignCountdownTriggerId,
  }
}

export const BonusCampaignDetailsForm = withFormik<OuterProps, EditCampaignDetailsFormValues>({
  mapPropsToValues: ({ data }) => ({
    id: data?.id ?? '',
    name: data?.name ?? '',
    minDeposit: data?.minDeposit ?? 0,
    maxBonus: data?.maxBonus ?? 0,
    termsAndConditionsUrl: data?.termsAndConditionsUrl ?? '',
    startDate: data?.startDate ?? '',
    endDate: data?.endDate ?? '',
    bonusExpirationDate: data?.bonusExpirationDate ?? '',
    bonusTypeId: data?.bonusType.id ?? '',
    bonusPercent: data?.bonusPercent ?? null,
    bonusAmount: data?.bonusAmount ?? null,
    bonusCampaignStatusId: data?.bonusCampaignStatus.id ?? 0,
    defaultCurrencyId: data?.defaultCurrency?.id ?? null,
    campaignPaymentMethods: data?.campaignPaymentMethods ?? [],
    tickmillCompanyIds: data?.tickmillCompanies.map((x) => x.id) ?? [],
    noPreviousDeposits: data?.noPreviousDeposits ?? false,
    availableOnlyOnce: data?.availableOnlyOnce ?? false,
    minTotalTradedLots: data?.minTotalTradedLots,
    maxTotalPnl: data?.maxTotalPnl,
    validDaysAfterActivation: data?.validDaysAfterActivation,
    firstTradeRequirementFromDays: data?.firstTradeRequirementFromDays ?? null,
    targetOnlyInactiveClients: data?.targetOnlyInactiveClients ?? false,
    bonusCampaignIntroducingBrokers: data?.bonusCampaignIntroducingBrokers.map((x) => x.name) ?? [],
    bonusCampaignAffiliates: data?.bonusCampaignAffiliates.map((x) => x.name) ?? [],
    bonusCampaignUtms: data?.bonusCampaignUtms ?? [],
    internalName: data?.internalName ?? '',
    bonusLabelId: data?.bonusLabel?.id ?? 0,
    bonusCampaignExcludeTypeId: data?.bonusCampaignExcludeType?.id ?? 0,
    bonusCampaignAvailabilityPeriodId: data?.bonusCampaignAvailabilityPeriod?.id ?? null,
    value: data?.value ?? null,
    bonusCampaignCountdownTriggerId: data?.bonusCampaignCountdownTrigger?.id ?? null,
  }),
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },
  enableReinitialize: true,
  validate: (values) => {
    const errors: FormikErrors<EditCampaignDetailsFormValues> = {}

    if (!values.internalName) errors.internalName = 'Internal Name is required'
    if (!values.bonusLabelId) errors.bonusLabelId = 'Bonus Label is required'
    if (!values.name) errors.name = 'Bonus Campaign Name is required'
    if (values.minDeposit <= 0) errors.minDeposit = 'Minimum deposit must be greater than 0'
    if (!UrlPattern.test(values.termsAndConditionsUrl)) {
      errors.termsAndConditionsUrl = 'Invalid URL format'
    }
    if (!values.startDate) errors.startDate = 'Start date is required'
    if (
      values.startDate &&
      values.endDate &&
      new Date(values.startDate) > new Date(values.endDate)
    ) {
      errors.startDate = 'Start date must be before end date'
    }
    if (!values.bonusTypeId) errors.bonusTypeId = 'Bonus Type is required'

    const hasAvailabilityPeriod =
      values.bonusCampaignAvailabilityPeriodId !== null &&
      values.bonusCampaignAvailabilityPeriodId > 0
    const hasValidDays = values.validDaysAfterActivation && values.validDaysAfterActivation > 0

    if (hasAvailabilityPeriod && hasValidDays) {
      errors.bonusCampaignAvailabilityPeriodId =
        'Cannot use both Available claim period and Valid days after activation'
      errors.validDaysAfterActivation =
        'Cannot use both Available claim period and Valid days after activation'
    }

    if (hasAvailabilityPeriod) {
      if (values.value === null || values.value <= 0) {
        errors.value = 'Value must be greater than 0'
      }
      if (!values.bonusCampaignCountdownTriggerId || values.bonusCampaignCountdownTriggerId <= 0) {
        errors.bonusCampaignCountdownTriggerId = 'Countdown Trigger is required'
      }
    }

    return errors
  },
})(BonusCampaignDetailsFormUI)
